import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAutoSave } from '../../hooks/useAutoSave';
import ProgressBar from '../../components/shared/ProgressBar';
import FileUpload from '../../components/shared/FileUpload';
import toast from 'react-hot-toast';
import { Save, ChevronLeft, ChevronRight, Check } from 'lucide-react';

const Checklist = () => {
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [sections, setSections] = useState([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [documents, setDocuments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const hasUnsavedChanges = Object.keys(answers).length > 0;

  const saveAnswers = async (currentAnswers) => {
    const answersArray = Object.entries(currentAnswers).map(([questionId, answerValue]) => ({
      questionId,
      answerValue
    }));

    await api.put('/submissions/my/answers', {
      answers: answersArray,
      currentSection: currentSectionIndex + 1
    });
  };

  const { lastSaved, isSaving, triggerSave } = useAutoSave(
    saveAnswers,
    hasUnsavedChanges,
    answers
  );

  useEffect(() => {
    fetchSubmission();
  }, []);

  const fetchSubmission = async () => {
    try {
      const response = await api.get('/submissions/my');
      if (response.data.success) {
        const data = response.data.data;
        setSubmission(data.submission);
        setSections(data.sections);
        setCurrentSectionIndex(data.submission.current_section - 1);

        // Initialize answers from existing data
        const initialAnswers = {};
        const initialDocuments = {};

        data.sections.forEach(section => {
          section.questions.forEach(question => {
            if (question.answer) {
              initialAnswers[question.id] = question.answer;
            }
          });
          section.document_requirements.forEach(req => {
            if (req.uploaded_documents && req.uploaded_documents.length > 0) {
              initialDocuments[req.id] = req.uploaded_documents;
            }
          });
        });

        setAnswers(initialAnswers);
        setDocuments(initialDocuments);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to load submission');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleDocumentUpload = (requirementId, document) => {
    setDocuments(prev => ({
      ...prev,
      [requirementId]: [...(prev[requirementId] || []), document]
    }));
  };

  const handleDocumentDelete = (requirementId, docId) => {
    setDocuments(prev => ({
      ...prev,
      [requirementId]: prev[requirementId]?.filter(doc => doc.id !== docId) || []
    }));
  };

  const handleNext = async () => {
    await triggerSave();
    if (currentSectionIndex < sections.length - 1) {
      setCurrentSectionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = async () => {
    await triggerSave();
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);

    try {
      await triggerSave();
      const response = await api.post('/submissions/my/submit');
      if (response.data.success) {
        navigate('/checklist/submitted');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveProgress = async () => {
    try {
      const toastId = toast.loading('Saving progress...');
      await triggerSave();
      toast.success('Progress saved successfully!', { id: toastId });
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  const canSubmit = () => {
    const currentSection = sections[currentSectionIndex];
    if (!currentSection) return false;

    // Check all required questions are answered
    const requiredQuestionsAnswered = currentSection.questions
      .filter(q => q.is_required)
      .every(q => answers[q.id]);

    // Check all required documents are uploaded
    const requiredDocsUploaded = currentSection.document_requirements
      .filter(dr => dr.is_required)
      .every(dr => documents[dr.id] && documents[dr.id].length > 0);

    return requiredQuestionsAnswered && requiredDocsUploaded;
  };

  const isSectionComplete = (sectionIndex) => {
    const section = sections[sectionIndex];
    if (!section) return false;

    const requiredQuestionsAnswered = section.questions
      .filter(q => q.is_required)
      .every(q => answers[q.id]);

    const requiredDocsUploaded = section.document_requirements
      .filter(dr => dr.is_required)
      .every(dr => documents[dr.id] && documents[dr.id].length > 0);

    return requiredQuestionsAnswered && requiredDocsUploaded;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  const currentSection = sections[currentSectionIndex];

  return (
    <div className="max-w-4xl mx-auto py-8">
      {/* Progress Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Broker Inspection Checklist</h1>
            <p className="text-gray-500 font-medium">{submission?.firm_name || 'Your Firm'}</p>
            {submission?.status === 'requires_resubmission' && (
              <p className="text-orange-500 text-sm mt-2 font-medium bg-orange-50 inline-block px-3 py-1 rounded-full">
                Resubmission Required - Please update based on inspector feedback
              </p>
            )}
          </div>
          <div className="text-right flex flex-col items-end">
            <button
              onClick={() => window.print()}
              className="flex items-center space-x-2 text-sm text-primary bg-primary/10 hover:bg-primary/20 px-3 py-1.5 rounded-lg font-semibold transition-colors mb-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
              <span>Export PDF</span>
            </button>
            <p className="text-sm font-medium text-gray-500">
              Page {currentSectionIndex + 1} of {sections.length}
            </p>
            {lastSaved && (
              <p className="text-xs text-gray-400 mt-1 font-medium">
                Last saved: {lastSaved.toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>
        <ProgressBar percentage={submission?.completion_percentage || 0} />
      </div>

      {/* Section Navigation */}
      <div className="flex flex-wrap gap-2 mb-8">
        {sections.map((section, index) => (
          <button
            key={section.id}
            onClick={() => {
              triggerSave();
              setCurrentSectionIndex(index);
            }}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
              index === currentSectionIndex
                ? 'bg-primary text-white shadow-md shadow-primary/20'
                : isSectionComplete(index)
                ? 'bg-green-50 text-primary hover:bg-green-100'
                : 'bg-white text-gray-500 border border-gray-200 hover:border-primary hover:text-primary shadow-sm'
            }`}
          >
            {index + 1}. {section.title}
          </button>
        ))}
      </div>

      {/* Section Content */}
      {currentSection && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
          <div
            className="mb-8 pb-6 border-b border-gray-100 pl-5 rounded-l-sm"
            style={{ borderLeftColor: currentSection.accent_color || '#039606', borderLeftWidth: '5px' }}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{currentSection.title}</h2>
            <p className="text-sm font-semibold text-gray-400 uppercase tracking-wider">{currentSection.department}</p>
            {currentSection.description && (
              <p className="text-gray-600 mt-3 text-sm leading-relaxed">{currentSection.description}</p>
            )}

            {currentSection.review && currentSection.review.status !== 'approved' && (
              <div className={`mt-4 p-4 rounded-md border ${currentSection.review.status === 'flagged' ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className={`h-5 w-5 ${currentSection.review.status === 'flagged' ? 'text-red-400' : 'text-orange-400'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className={`text-sm font-medium ${currentSection.review.status === 'flagged' ? 'text-red-800' : 'text-orange-800'}`}>
                      Inspector Feedback: {currentSection.review.status === 'flagged' ? 'Flagged' : 'Needs More Info'}
                    </h3>
                    <div className={`mt-2 text-sm ${currentSection.review.status === 'flagged' ? 'text-red-700' : 'text-orange-700'}`}>
                      <p>{currentSection.review.notes || 'No specific notes provided.'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Questions */}
          <div className="space-y-6 mb-8">
            {currentSection.questions.map((question) => (
              <div key={question.id} className="border border-gray-200 rounded-xl p-5 hover:border-primary transition-colors bg-gray-50/50">
                <label className="block text-sm font-medium text-gray-800 mb-3">
                  {question.order_index}. {question.question_text}
                  {question.is_required && <span className="text-red-500 ml-1">*</span>}
                </label>

                {question.question_type === 'yes_no' && (
                  <div className="flex space-x-6">
                    <label className="flex items-center cursor-pointer group">
                      <input
                        type="radio"
                        name={`question-${question.id}`}
                        value="yes"
                        checked={answers[question.id] === 'yes'}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        className="w-4 h-4 text-primary bg-white border-gray-300 focus:ring-primary focus:ring-2 cursor-pointer"
                      />
                      <span className="ml-2 text-gray-700 group-hover:text-gray-900 transition-colors">Yes</span>
                    </label>
                    <label className="flex items-center cursor-pointer group">
                      <input
                        type="radio"
                        name={`question-${question.id}`}
                        value="no"
                        checked={answers[question.id] === 'no'}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        className="w-4 h-4 text-primary bg-white border-gray-300 focus:ring-primary focus:ring-2 cursor-pointer"
                      />
                      <span className="ml-2 text-gray-700 group-hover:text-gray-900 transition-colors">No</span>
                    </label>
                  </div>
                )}

                {question.question_type === 'text' && (
                  <input
                    type="text"
                    value={answers[question.id] || ''}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow bg-white"
                    placeholder="Enter your answer"
                  />
                )}

                {question.question_type === 'textarea' && (
                  <textarea
                    value={answers[question.id] || ''}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none transition-shadow bg-white"
                    placeholder="Enter your answer"
                  />
                )}
              </div>
            ))}
          </div>

          {/* Document Requirements */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-dark">Required Documents</h3>
            {currentSection.document_requirements.map((req) => (
              <div key={req.id} className="border border-border rounded-lg p-4">
                <label className="block text-sm font-medium text-grey mb-3">
                  {req.order_index}. {req.name}
                  {req.is_required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {req.description && (
                  <p className="text-sm text-lightGrey mb-3">{req.description}</p>
                )}
                <FileUpload
                  requirementId={req.id}
                  sectionId={currentSection.id}
                  onUpload={(doc) => handleDocumentUpload(req.id, doc)}
                  existingDocuments={documents[req.id] || []}
                  onDelete={(docId) => handleDocumentDelete(req.id, docId)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
        <div className="flex space-x-3">
          <button
            onClick={handlePrevious}
            disabled={currentSectionIndex === 0 || isSaving}
            className="flex items-center space-x-2 px-6 py-3 border border-gray-200 rounded-full text-gray-600 hover:border-primary hover:text-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-white shadow-sm font-medium"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Previous</span>
          </button>
          <button
            onClick={handleSaveProgress}
            disabled={isSaving}
            className="flex items-center space-x-2 px-6 py-3 border border-gray-200 rounded-full text-gray-600 hover:border-primary hover:text-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-white shadow-sm font-medium"
          >
            <Save className="w-5 h-5" />
            <span>{isSaving ? 'Saving...' : 'Save Draft'}</span>
          </button>
        </div>

        {currentSectionIndex === sections.length - 1 ? (
          <button
            onClick={handleSubmit}
            disabled={!canSubmit() || submitting || isSaving}
            className="flex items-center space-x-2 px-8 py-3 bg-primary text-white rounded-full hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md font-bold tracking-wide"
          >
            <Check className="w-5 h-5" />
            <span>{submitting ? 'Submitting...' : 'Submit Checklist'}</span>
          </button>
        ) : (
          <button
            onClick={handleNext}
            disabled={isSaving}
            className="flex items-center space-x-2 px-8 py-3 bg-primary text-white rounded-full hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md font-bold tracking-wide"
          >
            <span>{isSaving ? 'Saving...' : 'Next'}</span>
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="mt-12 bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col md:flex-row items-center justify-between">
        <div className="mb-4 md:mb-0">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <svg className="w-5 h-5 mr-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            Need Help?
          </h3>
          <p className="text-gray-600 text-sm mt-1">Contact the LuSE team if you have any questions regarding this inspection checklist.</p>
        </div>
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
          <a href="mailto:legal@luse.co.zm" className="flex items-center text-sm font-medium text-gray-700 bg-gray-50 px-4 py-2 rounded-md hover:bg-gray-100 transition-colors border border-gray-200">
            <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            Legal: legal@luse.co.zm
          </a>
          <a href="mailto:itluse@luse.co.zm" className="flex items-center text-sm font-medium text-gray-700 bg-gray-50 px-4 py-2 rounded-md hover:bg-gray-100 transition-colors border border-gray-200">
            <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>
            IT: itluse@luse.co.zm
          </a>
        </div>
      </div>

      {/* Auto-save indicator */}
      {isSaving && (
        <div className="fixed bottom-4 right-4 bg-primary text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          <span>Auto-saving...</span>
        </div>
      )}
    </div>
  );
};

export default Checklist;
